import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography,
} from '@mui/material';
import { getActiveFees, checkout } from '../api/services';
import { useIsXs } from '../hooks/useMobileLayout';
import PageContainer from '../components/PageContainer';
import type { Fee } from '../../types/domain';
import { fmtCurrency, FEE_AGREEMENT_LABEL } from '../brand';

type NavState = { sindicatoId?: number | null; documentNumber?: string };

export default function Checkout() {
  const isXs = useIsXs();
  const { feeCode } = useParams();
  const location = useLocation() as { state?: NavState };
  const sindicatoIdFromNav = location.state?.sindicatoId;
  const documentNumber = location.state?.documentNumber?.trim() ?? '';
  const navigate = useNavigate();
  const code = (feeCode === 'AGREEMENT' || feeCode === 'NO_AGREEMENT') ? feeCode : null;
  const [fee, setFee] = useState<Fee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!code) {
        if (!cancelled) setLoading(false);
        return;
      }
      if (!documentNumber) {
        navigate('/home', {
          replace: true,
          state: { sindicatoId: sindicatoIdFromNav, feeCode: code },
        });
        if (!cancelled) setLoading(false);
        return;
      }
      if (!cancelled) setLoading(true);
      setError(null);
      try {
        const data = await getActiveFees({
          sindicatoId: sindicatoIdFromNav != null ? sindicatoIdFromNav : undefined,
        });
        if (cancelled) return;
        const found = code === 'AGREEMENT' ? data.agreement : data.noAgreement;
        setFee(found);
        if (!found) setError('No hay tarifa activa para esta opción');
      } catch (err: unknown) {
        if (cancelled) return;
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Error al cargar la tarifa';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [code, documentNumber, navigate, sindicatoIdFromNav]);

  const docLabel = useMemo(() => (fee?.documentType === 'PASSPORT' ? 'Pasaporte' : 'DNI'), [fee?.documentType]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!fee || !code || !documentNumber) return;
    setBusy(true);
    setError(null);
    try {
      const result = await checkout({
        feeCode: code,
        documentNumber,
        sindicatoId: sindicatoIdFromNav,
      });
      navigate(`/mp-mock/${result.payment.paymentId}`, {
        state: { payment: result.payment, fee },
        replace: true,
      });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'No se pudo procesar el cobro';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  if (!code) {
    return (
      <PageContainer maxWidth="sm" sx={{ py: 2 }}>
        <Alert severity="error">Código de tarifa no válido.</Alert>
      </PageContainer>
    );
  }

  if (!documentNumber) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  return (
    <PageContainer maxWidth="sm" sx={{ py: 2 }}>
      <Paper sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
          Importe y pago
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {fee && (
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">{docLabel}</Typography>
              <Chip size="small" label={documentNumber} color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
            </Stack>
            <Box>
              <Typography variant="overline" color="text.secondary">Importe</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{fmtCurrency(fee.amount, fee.currency)}</Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip size="small" label={`Tipo: ${fee.name}`} />
              <Chip size="small" color={fee.isGlobalFallback ? 'warning' : 'success'} label={fee.isGlobalFallback ? 'Tarifa global' : 'Tarifa de la empresa'} />
            </Stack>
          </Stack>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: 1 }}>
            <Button
              type="button"
              onClick={() => navigate('/home', { state: { sindicatoId: sindicatoIdFromNav, feeCode: code } })}
              variant="outlined"
              fullWidth={isXs}
              sx={{ flex: { sm: '0 0 auto' } }}
            >
              Volver
            </Button>
            <Button type="submit" variant="contained" disabled={busy || !fee} fullWidth={isXs} sx={{ flex: { sm: 1 } }}>
              {busy ? 'Procesando...' : 'Pagar (Mercado Pago simulado)'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </PageContainer>
  );
}
